import { useState } from "react";

import { NotificationExpandToggle } from "./NotificationExpandToggle";
import { Column, Text } from "@/components";

import { formatting } from "@/lib/formatting";
import { MIXPANEL_NOTIFICATION_SURFACES } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  NotificationLeaderboardWeeklyPayload,
  NotificationLeaderboardWeeklyWinner,
  NotificationType,
} from "@/types";

export const NotificationLeaderboardWeeklyList = ({
  isCompact = false,
  payload,
  trackingSurface,
}: {
  isCompact?: boolean;
  payload: NotificationLeaderboardWeeklyPayload;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { revenue, placements, fastestTimeToFill } = payload;

  const winnerNames = (
    winners: NotificationLeaderboardWeeklyWinner[],
  ): string => winners.map((winner) => winner.fullName).join(", ");

  // One row per category that had a winner this week; empty categories skipped.
  const rows: { label: string; value: string; names: string; emoji: string }[] =
    [];
  if (revenue) {
    rows.push({
      label: "Most Revenue",
      value: formatting.currency(revenue.amount),
      names: winnerNames(revenue.winners),
      emoji: "💰",
    });
  }
  if (placements) {
    rows.push({
      label: "Most Placements",
      value: placements.count.toString(),
      names: winnerNames(placements.winners),
      emoji: "🤝",
    });
  }
  if (fastestTimeToFill) {
    rows.push({
      label: "Fastest Time to Fill",
      value: `${fastestTimeToFill.days.toString()} ${fastestTimeToFill.days === 1 ? "day" : "days"}`,
      names: winnerNames(fastestTimeToFill.winners),
      emoji: "⚡",
    });
  }

  const render = () => (
    <Column className="gap-0.5">
      <NotificationExpandToggle
        isExpanded={isExpanded}
        notificationType={NotificationType.LEADERBOARD_WEEKLY}
        setIsExpanded={setIsExpanded}
        trackingSurface={trackingSurface}
      >
        View winners
      </NotificationExpandToggle>
      {isExpanded ? (
        <Column className="gap-1 border-l border-slate-200 pl-3 pt-1">
          {rows.map((row) => (
            <Column key={row.label} className="gap-0 px-2 py-1.5">
              <Text className={cn("text-sm", isCompact && "text-xs")}>
                {row.names}
              </Text>
              <Text className="text-xs font-normal text-slate-500">
                {row.label} · {row.value} {row.emoji}
              </Text>
            </Column>
          ))}
        </Column>
      ) : null}
    </Column>
  );

  return render();
};
